import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Wallet, AlertCircle } from 'lucide-react';
import { useAuth } from '../App';
import toast from 'react-hot-toast';
import './Auth.css';

const Login = () => {
  const [walletId, setWalletId] = useState('');
  const [lastWord, setLastWord] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { loginUser, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!walletId.trim() || !lastWord.trim()) {
      setError('Please enter your Wallet ID and the last word of your recovery phrase.');
      return;
    }

    setIsLoading(true);
    try {
      const result = await loginUser({ walletId: walletId.trim(), lastWord: lastWord.trim() });

      if (result.error === 'suspended') {
        toast.error('Your account has been suspended. Please contact support.');
        return;
      }

      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (err) {
      if (err.code === 'NOT_FOUND') {
        setError('No account found with that Wallet ID.');
      } else if (err.code === 'LOCKED') {
        setError(err.message);
      } else if (err.code === 'WRONG_WORD') {
        setError('Incorrect word. Please try again.');
      } else {
        setError(err.message || 'Login failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        {/* Left Side - Branding */}
        <div className="auth-branding">
          <div className="branding-content">
            <Link to="/" className="auth-logo">
              <div className="logo-icon">
                <Shield size={24} />
              </div>
              <span className="logo-text">
                Web3<span className="logo-highlight">Ledger</span>
              </span>
            </Link>

            <div className="branding-text">
              <h1>Welcome<br />Back</h1>
              <p>Sign in to access your secure dashboard and manage your digital assets.</p>
            </div>

            <div className="login-features">
              <div className="login-feature">
                <span className="feature-icon">🔐</span>
                <div className="feature-text">
                  <strong>Secure Access</strong>
                  <span>Multi-layer authentication</span>
                </div>
              </div>
              <div className="login-feature">
                <span className="feature-icon">⚡</span>
                <div className="feature-text">
                  <strong>Instant Sync</strong>
                  <span>Real-time portfolio updates</span>
                </div>
              </div>
              <div className="login-feature">
                <span className="feature-icon">🌐</span>
                <div className="feature-text">
                  <strong>Multi-Chain</strong>
                  <span>50+ networks supported</span>
                </div>
              </div>
            </div>

            <div className="branding-footer">
              <p>Protected by enterprise-grade security</p>
            </div>
          </div>

          <div className="branding-bg">
            <div className="bg-gradient"></div>
            <div className="bg-pattern"></div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="auth-form-section">
          <div className="auth-form-container">
            <Link to="/" className="back-link">
              <ArrowLeft size={18} />
              Back to Home
            </Link>

            <div className="auth-form-content">
              <div className="form-header">
                <h2>Login</h2>
                <p>Sign in with your Wallet ID and recovery phrase</p>
              </div>

              <form className="quick-login-form pin-login-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="login-wallet-id">Wallet ID</label>
                  <div className="pin-input-wrap">
                    <Wallet size={18} aria-hidden="true" />
                    <input
                      id="login-wallet-id"
                      type="text"
                      autoComplete="username"
                      placeholder="W3L-00000000"
                      value={walletId}
                      onChange={(e) => { setWalletId(e.target.value); setError(''); }}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="login-last-word">Last Word of Your Phrase</label>
                  <div className="pin-input-wrap">
                    <Lock size={18} aria-hidden="true" />
                    <input
                      id="login-last-word"
                      type="password"
                      autoComplete="current-password"
                      placeholder="Enter the last word"
                      value={lastWord}
                      onChange={(e) => { setLastWord(e.target.value); setError(''); }}
                      required
                    />
                  </div>
                  <span className="input-hint">The final word of your 12 or 24 word recovery phrase</span>
                </div>

                {error && (
                  <div className="error-message">
                    <AlertCircle size={16} />
                    <span>{error}</span>
                  </div>
                )}

                <button type="submit" className="btn btn-primary pin-login-submit" disabled={isLoading}>
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>

              <p className="security-note">
                🔒 Your credentials are encrypted with AES-256 and verified securely.
              </p>

              <div className="auth-footer">
                <p>
                  Don't have an account?{' '}
                  <Link to="/get-started">Get Started</Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
